import { lazy, ComponentType } from 'react';

// Dopo un deploy i chunk della build precedente non esistono più sul server:
// gli utenti con una tab aperta otterrebbero un ChunkLoadError alla prima
// navigazione verso una pagina lazy. Strategia: al primo fallimento si
// ricarica la pagina (una sola volta, guard su sessionStorage) così il
// browser scarica l'index.html nuovo con i riferimenti ai chunk correnti.

const RELOAD_FLAG = 'unasca-chunk-reload';

type Factory<T extends ComponentType<any>> = () => Promise<{ default: T }>;

export interface RetryDeps {
  getFlag: () => boolean;
  setFlag: () => void;
  clearFlag: () => void;
  reload: () => void;
}

// sessionStorage può lanciare (es. Safari private mode): ogni accesso è guarded
const defaultDeps: RetryDeps = {
  getFlag: () => {
    try { return sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch { return false; }
  },
  setFlag: () => {
    try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch {}
  },
  clearFlag: () => {
    try { sessionStorage.removeItem(RELOAD_FLAG); } catch {}
  },
  reload: () => window.location.reload(),
};

export async function importWithReload<T extends ComponentType<any>>(
  factory: Factory<T>,
  deps: RetryDeps = defaultDeps
): Promise<{ default: T }> {
  try {
    const mod = await factory();
    deps.clearFlag();
    return mod;
  } catch (error) {
    if (!deps.getFlag()) {
      deps.setFlag();
      deps.reload();
      // Mentre il browser ricarica, Suspense continua a mostrare il fallback
      return new Promise<never>(() => {});
    }
    throw error;
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(factory: Factory<T>) {
  return lazy(() => importWithReload(factory));
}

export default lazyWithRetry;
